// Engine Service - GraphQL operations for Engine role
import { graphqlClient } from '../graphqlClient';

// ==================== Profile ====================

export const getProfile = async () => {
  const query = `
    query GetEngineProfile {
      engineProfile {
        id
        email
        name
        status
        code
        depositCommission
        withdrawalCommission
        percentageFee
        parentName
        parentType
        parentDepositCommission
        parentWithdrawalCommission
        parentPercentageFee
        createdAt
        updatedAt
      }
    }
  `;
  
  const result = await graphqlClient.query(query);
  return result.engineProfile;
};

// ==================== Reseller Operations ====================

export const getMyResellers = async () => {
  const query = `
    query GetEngineResellers {
      engineResellers {
        id
        code
        email
        name
        status
        createdByAdminId
        createdByEngineId
        createdAt
        updatedAt
      }
    }
  `;
  
  const result = await graphqlClient.query(query);
  return result.engineResellers;
};

export const getResellerById = async (id) => {
  const query = `
    query GetEngineResellerById($id: String!) {
      engineResellerById(id: $id) {
        id
        code
        email
        name
        status
        depositCommission
        withdrawalCommission
        percentageFee
        createdAt
        updatedAt
      }
    }
  `;
  
  const result = await graphqlClient.query(query, { id });
  return result.engineResellerById;
};

export const createReseller = async (input) => {
  const mutation = `
    mutation EngineCreateReseller($input: EngineCreateResellerInput!) {
      engineCreateReseller(input: $input) {
        id
        code
        email
        name
        status
        createdAt
        updatedAt
      }
    }
  `;
  
  const result = await graphqlClient.mutate(mutation, { input });
  return result.engineCreateReseller;
};

export const updateReseller = async (id, input) => {
  const mutation = `
    mutation EngineUpdateReseller($id: String!, $input: EngineUpdateResellerInput!) {
      engineUpdateReseller(id: $id, input: $input) {
        id
        code
        email
        name
        status
        createdAt
        updatedAt
      }
    }
  `;
  
  const result = await graphqlClient.mutate(mutation, { id, input });
  return result.engineUpdateReseller;
};

export const deleteReseller = async (id) => {
  const mutation = `
    mutation EngineDeleteReseller($id: String!) {
      engineDeleteReseller(id: $id)
    }
  `;
  
  const result = await graphqlClient.mutate(mutation, { id });
  return result.engineDeleteReseller;
};

// ==================== Merchant Operations ====================

export const getMyMerchants = async () => {
  const query = `
    query GetEngineMerchants {
      engineMerchants {
        id
        code
        email
        name
        status
        createdByAdminId
        createdByEngineId
        createdByResellerId
        currency
        fee_type
        fee_rate
        createdAt
        updatedAt
      }
    }
  `;
  
  const result = await graphqlClient.query(query);
  return result.engineMerchants;
};

export const getMerchantsByEngineId = async (engineId) => {
  const query = `
    query GetEngineMerchantsByEngineId($engineId: String!) {
      engineMerchantsByEngineId(engineId: $engineId)
    }
  `;
  const result = await graphqlClient.query(query, { engineId });
  return result.engineMerchantsByEngineId;
};

export const getMerchantById = async (id) => {
  const query = `
    query GetEngineMerchantById($id: String!) {
      engineMerchantById(id: $id) {
        id
        code
        email
        name
        status
        currency
        fee_type
        fee_rate
        createdAt
        updatedAt
      }
    }
  `;
  
  const result = await graphqlClient.query(query, { id });
  return result.engineMerchantById;
};

export const getResellerMerchants = async (resellerId) => {
  const query = `
    query GetEngineResellerMerchants($resellerId: String!) {
      engineResellerMerchants(resellerId: $resellerId) {
        id
        code
        email
        name
        status
        createdByAdminId
        createdByEngineId
        createdByResellerId
        currency
        fee_type
        fee_rate
        createdAt
        updatedAt
      }
    }
  `;

  const result = await graphqlClient.query(query, { resellerId });
  return result.engineResellerMerchants;
};

export const createMerchant = async (input) => {
  const mutation = `
    mutation EngineCreateMerchant($input: EngineCreateMerchantInput!) {
      engineCreateMerchant(input: $input) {
        id
        code
        email
        name
        status
        currency
        fee_type
        fee_rate
        createdAt
        updatedAt
      }
    }
  `;
  
  const result = await graphqlClient.mutate(mutation, { input });
  return result.engineCreateMerchant;
};

export const updateMerchant = async (id, input) => {
  const mutation = `
    mutation EngineUpdateMerchant($id: String!, $input: EngineUpdateMerchantInput!) {
      engineUpdateMerchant(id: $id, input: $input) {
        id
        code
        email
        name
        status
        currency
        fee_type
        fee_rate
        createdAt
        updatedAt
      }
    }
  `;
  
  const result = await graphqlClient.mutate(mutation, { id, input });
  return result.engineUpdateMerchant;
};

export const deleteMerchant = async (id) => {
  const mutation = `
    mutation EngineDeleteMerchant($id: String!) {
      engineDeleteMerchant(id: $id)
    }
  `;
  
  const result = await graphqlClient.mutate(mutation, { id });
  return result.engineDeleteMerchant;
};

// ==================== Dashboard Stats ====================

export const getDashboardStats = async () => {
  const query = `
    query GetEngineDashboardStats {
      engineDashboardStats {
        totalResellers
        totalMerchants
        activeResellers
        activeMerchants
      }
    }
  `;
  
  const result = await graphqlClient.query(query);
  return result.engineDashboardStats;
};

// ==================== Webhook Logs ====================

export const getWebhookLogs = async (page = 1, limit = 50) => {
  const query = `
    query GetEngineWebhookLogs($page: Int, $limit: Int) {
      engineWebhookLogs(page: $page, limit: $limit) {
        id
        qrCodeId
        paymentId
        merchantId
        merchantName
        event
        url
        httpStatus
        responseBody
        errorMessage
        status
        direction
        requestPayload
        createdAt
      }
    }
  `;

  const result = await graphqlClient.query(query, { page, limit });
  return result.engineWebhookLogs || [];
};

